import PropTypes from 'prop-types';
import { FunctionField, SingleFieldList, TextField, usePermissions, useRecordContext, useTranslate } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { DetailsShow } from '@/components/global/drawers/DetailsShow.jsx';
import { DetailsActionsPopover } from '@/components/global/buttons/DetailsActionsPopover.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomChipField from '@/components/global/fields/CustomChipField.jsx';
import { TagsArrayField } from '@/components/tagCategory/TagsArrayField.jsx';
import { useDrawer } from '@/composables/useDrawer.js';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';
import { colorChoices } from '@/constants/TagCategoryColorChoices.js';
import { formatTagHistoryChanges } from '@/components/tagCategory/formatTagHistoryChanges.js';

const TagCategoryDetailsActions = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const { openDrawer } = useDrawer();

    if (!permissions?.isAdmin || !record?.id) {
        return null;
    }

    return (
        <DetailsActionsPopover>
            <CustomEditModalButton label={translate('resources.tagCategory.labels.editTags')} onOpen={() => openDrawer(record.id, 'tag-category-edit')} />
        </DetailsActionsPopover>
    );
};

export const TagCategoryShow = ({ handleClose, elementId }) => {
    const translate = useTranslate();

    return (
        <DetailsShow
            handleClose={handleClose}
            elementId={elementId}
            resource='tag-category'
            canView={permissions => canViewDetails('tag-category', permissions)}
            actions={<TagCategoryDetailsActions />}
            showChangeHistory
            historyFieldsSource='resources.tagCategory.fields'
            formatHistoryFieldChanges={formatTagHistoryChanges}
        >
            <TextField source='name' label={translate('resources.tagCategory.fields.namePl')} />
            <TextField source='nameEn' label={translate('resources.tagCategory.fields.nameEn')} />
            <FunctionField
                label={translate('common.color')}
                render={record => {
                    const choice = colorChoices.find(item => item.id === record?.color);
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Box sx={{ width: 20, height: 20, borderRadius: '4px', backgroundColor: record?.color }} />
                            <Typography variant='body2' sx={{ fontSize: '14px' }}>
                                {choice ? translate(choice.name) : record?.color}
                            </Typography>
                        </Box>
                    );
                }}
            />
            <TagsArrayField source='tags' label={translate('resources.product.fields.tags')}>
                <SingleFieldList linkType={false} className='tag-list'>
                    <CustomChipField shades={true} />
                </SingleFieldList>
            </TagsArrayField>
            <TagsArrayField source='tags' label={translate('resources.tagCategory.fields.tagsEn')}>
                <SingleFieldList linkType={false} className='tag-list'>
                    <CustomChipField source='nameEn' shades={true} />
                </SingleFieldList>
            </TagsArrayField>
        </DetailsShow>
    );
};

TagCategoryShow.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
